import styles from "./bulkSendPaymentHistoryModal.module.css";
import { IPopupWithoutProps } from "../../../types/types";
import venchyImg from "../../../images/questionnaire_doctor.png";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import { useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { useSendPaymentHistoryLinkBulkMutation } from "../../../services/api/utils.api";
import { IUserReport } from "../../../services/types/users.types";

interface BulkSendPaymentHistoryModalProps extends IPopupWithoutProps {
  selectedUserIds: Set<number>;
  userList: IUserReport[];
  companyId: number;
}

type DatePreset = "all" | "3months" | "6months" | "1year" | "custom";

const LARGE_BATCH_THRESHOLD = 50;

function BulkSendPaymentHistoryModal({
  onClose,
  selectedUserIds,
  userList,
  companyId,
}: BulkSendPaymentHistoryModalProps) {
  const isRtl = i18n.dir() === "rtl";

  const [sendViaSms, setSendViaSms] = useState(true);
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [customFromDate, setCustomFromDate] = useState<DateObject | null>(null);
  const [customToDate, setCustomToDate] = useState<DateObject | null>(null);

  const [sendBulk, { isLoading }] = useSendPaymentHistoryLinkBulkMutation();

  const [results, setResults] = useState<{
    totalSent: number;
    totalFailed: number;
    failed: { clientId: number; error: string }[];
  } | null>(null);

  const getUserName = (clientId: number) => {
    const u = userList.find((ur) => ur.Users.Id === clientId);
    if (!u) return `#${clientId}`;
    return u.Users.FullName || `${u.Users.FirstName} ${u.Users.LastName || ""}`.trim();
  };

  const getErrorText = (error: string) => {
    switch (error) {
      case "client_has_no_phone": return t("CLIENT_HAS_NO_PHONE");
      case "client_has_no_email": return t("CLIENT_HAS_NO_EMAIL");
      case "client_not_found": return t("BULK_SEND_ERROR_CLIENT_NOT_FOUND");
      case "unauthorized": return t("BULK_SEND_ERROR_UNAUTHORIZED");
      default: return t("BULK_SEND_ERROR_INTERNAL");
    }
  };

  const handleSend = async () => {
    if (!sendViaSms && !sendViaEmail) return;

    const deliveryChannel = sendViaSms && sendViaEmail ? 3 : sendViaSms ? 1 : 2;

    let fromDate: string | undefined;
    let toDate: string | undefined;
    const now = new Date();

    if (datePreset === "3months") {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      fromDate = from.toISOString();
    } else if (datePreset === "6months") {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 6);
      fromDate = from.toISOString();
    } else if (datePreset === "1year") {
      const from = new Date(now);
      from.setFullYear(from.getFullYear() - 1);
      fromDate = from.toISOString();
    } else if (datePreset === "custom") {
      if (!customFromDate && !customToDate) return;
      if (customFromDate) fromDate = customFromDate.toDate().toISOString();
      if (customToDate) toDate = customToDate.toDate().toISOString();
    }

    try {
      const response = await sendBulk({
        clientIds: Array.from(selectedUserIds),
        companyId,
        deliveryChannel,
        fromDate,
        toDate,
      }).unwrap();

      setResults({
        totalSent: response.totalSent,
        totalFailed: response.totalFailed,
        failed: response.results
          .filter((r) => !r.success)
          .map((r) => ({ clientId: r.clientId, error: r.error || "internal_error" })),
      });
    } catch {
      setResults({
        totalSent: 0,
        totalFailed: selectedUserIds.size,
        failed: [{ clientId: 0, error: "internal_error" }],
      });
    }
  };

  const canSend = (sendViaSms || sendViaEmail) && !(datePreset === "custom" && !customFromDate && !customToDate);

  return (
    <article className={styles.popup} data-testid="bulk-send-payment-history-popup">
      <img className={styles.popup__img} src={venchyImg} alt={t("VENCHI")} />
      <h2 className={styles.popup__title}>{t("BULK_SEND_PAYMENT_HISTORY_TITLE")}</h2>
      <p className={styles.popup__text}>
        {t("BULK_SEND_PAYMENT_HISTORY_DESC", { count: selectedUserIds.size })}
      </p>

      {!results && (
        <>
          {selectedUserIds.size >= LARGE_BATCH_THRESHOLD && (
            <div className={styles.warningBanner}>
              {t("BULK_SEND_WARNING_LARGE_BATCH", { count: selectedUserIds.size })}
            </div>
          )}
          <div className={styles.controls}>
            {/* Delivery channel */}
            <div className={styles.controlRow}>
              <span className={styles.controlLabel}>{t("SEND_PAYMENT_HISTORY")}:</span>
              <label className={styles.channelCheckbox}>
                <input
                  type="checkbox"
                  checked={sendViaSms}
                  onChange={(e) => setSendViaSms(e.target.checked)}
                  disabled={isLoading}
                />
                SMS
              </label>
              <label className={styles.channelCheckbox}>
                <input
                  type="checkbox"
                  checked={sendViaEmail}
                  onChange={(e) => setSendViaEmail(e.target.checked)}
                  disabled={isLoading}
                />
                {t("EMAIL")}
              </label>
            </div>

            {/* Date range */}
            <div className={styles.controlRow}>
              <span className={styles.controlLabel}>{t("DATE_RANGE")}:</span>
              <select
                className={styles.dateSelect}
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value as DatePreset)}
                disabled={isLoading}
                style={{ direction: isRtl ? "rtl" : "ltr" }}
              >
                <option value="all">{t("DATE_RANGE_ALL")}</option>
                <option value="3months">{t("DATE_RANGE_3_MONTHS")}</option>
                <option value="6months">{t("DATE_RANGE_6_MONTHS")}</option>
                <option value="1year">{t("DATE_RANGE_1_YEAR")}</option>
                <option value="custom">{t("DATE_RANGE_CUSTOM")}</option>
              </select>
            </div>

            {datePreset === "custom" && (
              <>
                <div className={styles.controlRow}>
                  <span className={styles.controlLabel}>{t("FROM")}:</span>
                  <DatePicker
                    value={customFromDate}
                    onChange={(val: DateObject) => setCustomFromDate(val)}
                    format="DD/MM/YYYY"
                    disabled={isLoading}
                    maxDate={customToDate || new DateObject()}
                    portal
                    style={{
                      fontSize: "12px",
                      padding: "3px 6px",
                      borderRadius: "4px",
                      border: "1px solid #cbd5e1",
                      width: "110px",
                      textAlign: "center",
                    }}
                    containerStyle={{ direction: "ltr" }}
                  />
                </div>
                <div className={styles.controlRow}>
                  <span className={styles.controlLabel}>{t("TO")}:</span>
                  <DatePicker
                    value={customToDate}
                    onChange={(val: DateObject) => setCustomToDate(val)}
                    format="DD/MM/YYYY"
                    disabled={isLoading}
                    minDate={customFromDate || undefined}
                    maxDate={new DateObject()}
                    portal
                    style={{
                      fontSize: "12px",
                      padding: "3px 6px",
                      borderRadius: "4px",
                      border: "1px solid #cbd5e1",
                      width: "110px",
                      textAlign: "center",
                    }}
                    containerStyle={{ direction: "ltr" }}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.popup__buttons}>
            <button
              className={`button ${styles.popup__confirmBtn} hover`}
              data-testid="confirm-bulk-send-button"
              onClick={handleSend}
              disabled={isLoading || !canSend}
            >
              {isLoading ? t("SENDING") + "..." : t("SEND_PAYMENT_HISTORY")}
            </button>
            <button
              className={`button ${styles.popup__cancelBtn} hover`}
              data-testid="cancel-bulk-send-button"
              onClick={onClose}
              disabled={isLoading}
            >
              {t("CANCEL")}
            </button>
          </div>
        </>
      )}

      {results && (
        <div className={styles.results}>
          <div className={styles.resultsSummary}>
            <span className={styles.resultsSent}>
              {t("BULK_SEND_RESULTS_SENT", { count: results.totalSent })}
            </span>
            {results.totalFailed > 0 && (
              <span className={styles.resultsFailed}>
                {t("BULK_SEND_RESULTS_FAILED", { count: results.totalFailed })}
              </span>
            )}
          </div>

          {results.failed.length > 0 && (
            <div className={styles.failedList}>
              <div className={styles.failedListTitle}>{t("BULK_SEND_FAILED_DETAILS")}:</div>
              {results.failed.map((f, i) => (
                <div key={i} className={styles.failedItem}>
                  {f.clientId > 0 ? getUserName(f.clientId) : ""} — {getErrorText(f.error)}
                </div>
              ))}
            </div>
          )}

          <div className={styles.popup__buttons} style={{ marginTop: "16px" }}>
            <button
              className={`button ${styles.popup__cancelBtn} hover`}
              onClick={onClose}
            >
              {t("CLOSE_BUTTON")}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export default BulkSendPaymentHistoryModal;
